import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { GoogleButton } from "@/components/GoogleButton";
import { LocationInput } from "@/components/LocationInput";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "seeker", city: "", country: "", interests: [], interests_other: "" });
  const [categories, setCategories] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  const toggleInterest = (name) => {
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(name) ? f.interests.filter((i) => i !== name) : [...f.interests, name],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await register(form);
      toast.success("Welcome to the community");
      navigate(location.state?.from || (user.role === "practitioner" ? "/dashboard" : "/"));
    } catch (err) {
      toast.error(apiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="silk-hero min-h-[70vh] flex items-center justify-center px-5 py-20">
      <div className="w-full max-w-md silk-card rounded-lg p-8 sm:p-10">
        <h1 className="font-display text-4xl text-foreground mb-2">Join O.R.E.C</h1>
        <p className="text-sm text-muted-foreground mb-8">Create your free account and become part of the Ripple.</p>
        {localStorage.getItem("orec_ref") && (
          <p className="text-xs text-primary border border-primary/30 rounded-md px-3 py-2 mb-6" data-testid="register-ref-banner">
            You've been invited — your signup adds to a friend's Ripple. ✦
          </p>
        )}

        <div className="grid grid-cols-2 gap-2 mb-7" data-testid="register-role-toggle">
          {[["seeker", "I'm a seeker"], ["practitioner", "I'm a practitioner"]].map(([role, label]) => (
            <button key={role} type="button" data-testid={`register-role-${role}`}
              onClick={() => setForm({ ...form, role })}
              className={`rounded-full py-2.5 text-sm border transition-colors ${form.role === role ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
              {label}
            </button>
          ))}
        </div>

        <GoogleButton role={form.role} label={`Continue with Google as ${form.role}`} />
        <div className="flex items-center gap-3 my-6">
          <div className="h-px flex-1 bg-border" /><span className="text-xs text-muted-foreground">or</span><div className="h-px flex-1 bg-border" />
        </div>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label htmlFor="name" className="text-xs uppercase tracking-wider text-muted-foreground">Name</Label>
            <Input id="name" data-testid="register-name-input" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2" />
          </div>
          <div>
            <Label htmlFor="email" className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
            <Input id="email" data-testid="register-email-input" type="email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2" />
          </div>
          <div>
            <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground">Password (min 6 characters)</Label>
            <Input id="password" data-testid="register-password-input" type="password" required minLength={6} value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-2" />
          </div>

          {form.role === "seeker" && (
            <>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Your location</Label>
                <div className="mt-2">
                  <LocationInput idPrefix="register" city={form.city} country={form.country}
                    onChange={({ city, country }) => setForm((f) => ({ ...f, city, country }))} />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">What are you interested in?</Label>
                <div className="grid grid-cols-1 gap-2.5 mt-3" data-testid="register-interests">
                  {categories.map((c) => (
                    <label key={c.id} className="flex items-center gap-2.5 text-sm text-muted-foreground cursor-pointer hover:text-foreground">
                      <Checkbox checked={form.interests.includes(c.name)} onCheckedChange={() => toggleInterest(c.name)}
                        data-testid={`register-interest-${c.slug || c.id}`} />
                      {c.name}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label htmlFor="other" className="text-xs uppercase tracking-wider text-muted-foreground">Other interests (optional)</Label>
                <Input id="other" data-testid="register-other-input" value={form.interests_other} placeholder="Anything else you're drawn to…"
                  onChange={(e) => setForm({ ...form, interests_other: e.target.value })} className="mt-2" />
              </div>
            </>
          )}

          <Button data-testid="register-submit-btn" type="submit" disabled={busy} className="w-full h-11 rounded-full">
            {busy ? "Creating…" : "Create account"}
          </Button>
        </form>
        {form.role === "practitioner" && (
          <p className="text-xs text-muted-foreground mt-5 leading-relaxed">After signup you'll create your profile. Every practitioner is personally reviewed by the O.R.E.C team before going live — that's the vetted promise. <Link to="/how-joining-works" className="cream-link">See how joining works</Link>.</p>
        )}
        <p className="text-sm text-muted-foreground mt-6 text-center">
          Already a member? <Link to="/login" className="cream-link" data-testid="register-login-link">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
